import { useEffect, useState, useRef } from 'react';
import '@/App.css';
import { toast } from 'sonner';
import { initAnalytics, identifyUser, Analytics } from '@/analytics';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { User } from '@/components/HypIcons';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

// ── Nav SVG icons (unique, minimal, 22×22) ────────────────────────────────
const NavIcons = {
  home: (active) => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M3 10.5L11 3L19 10.5V19.5H14.5V14H7.5V19.5H3V10.5Z"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5"
        strokeLinejoin="round" fill={active ? 'rgba(30,107,104,0.1)' : 'none'}/>
    </svg>
  ),
  journal: (active) => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="4" y="3" width="12" height="16" rx="2"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5"
        fill={active ? 'rgba(30,107,104,0.08)' : 'none'}/>
      <line x1="7.5" y1="8"  x2="13.5" y2="8"  stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="7.5" y1="11" x2="13.5" y2="11" stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="7.5" y1="14" x2="11"   y2="14" stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Ribbon */}
      <rect x="17" y="3" width="2.5" height="16" rx="1"
        fill={active ? '#1E6B68' : '#B8D3CF'} opacity={active ? 1 : 0.5}/>
    </svg>
  ),
  insights: (active) => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="2.5"  y="14" width="4" height="5.5" rx="1"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.4"
        fill={active ? 'rgba(30,107,104,0.12)' : 'none'}/>
      <rect x="9"    y="9"  width="4" height="10.5" rx="1"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.4"
        fill={active ? 'rgba(30,107,104,0.12)' : 'none'}/>
      <rect x="15.5" y="4"  width="4" height="15.5" rx="1"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.4"
        fill={active ? 'rgba(30,107,104,0.12)' : 'none'}/>
    </svg>
  ),
  studio: (active) => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="8" y="2.5" width="6" height="10" rx="3"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5"
        fill={active ? 'rgba(30,107,104,0.08)' : 'none'}/>
      <path d="M5 11 C5 15.4 17 15.4 17 11"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <line x1="11" y1="15.4" x2="11" y2="18.5"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="8" y1="18.5" x2="14" y2="18.5"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  circle: (active) => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="8"
        stroke={active ? '#1E6B68' : '#AAB8B6'} strokeWidth="1.5"
        fill={active ? 'rgba(30,107,104,0.07)' : 'none'}/>
      <circle cx="11" cy="11" r="3"
        fill={active ? '#1E6B68' : '#AAB8B6'} opacity={active ? 0.7 : 0.4}/>
    </svg>
  ),
};
import Home from '@/pages/Home';
import LandingPage from '@/pages/LandingPage';
import IntroExperience from '@/pages/IntroExperience';
import Alignment from '@/pages/Alignment';
import VoiceLab from '@/pages/VoiceLab';
import MindMirror from '@/pages/MindMirror';
import TransformationSuite from '@/pages/TransformationSuite';
import PsychedeliaLivingRoom from '@/pages/PsychedeliaLivingRoom';
import PsychedeliaAdmin from '@/pages/PsychedeliaAdmin';
import CreateProfile from '@/pages/CreateProfile';
import Callback from '@/pages/Callback';
import ERIPreview from '@/pages/ERIPreview';
import About from '@/pages/About';
import Insights from '@/pages/Insights';
import WindowOfTolerance from '@/pages/WindowOfTolerance';
import PatternIntelligence from '@/pages/PatternIntelligence';
import ClinicalReport from '@/pages/ClinicalReport';
import PractitionerPortal from '@/pages/PractitionerPortal';
import EmotionalTimeline from '@/pages/EmotionalTimeline';
import TherapistDashboard from '@/pages/TherapistDashboard';
import DoctorDashboard from '@/pages/DoctorDashboard';
import ClinicalAssessment from '@/pages/ClinicalAssessment';
import OnboardingRitual from '@/pages/OnboardingRitual';
import Programs from '@/pages/Programs';
import ProofOfChange from '@/pages/ProofOfChange';
import MyMeditations from '@/pages/MyMeditations';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Guide from '@/pages/Guide';
import Terms from '@/pages/Terms';
import AccountSettings from '@/pages/AccountSettings';
import Research from '@/pages/Research';
import MomentButton from '@/components/MomentButton';
import { initSmartNotifications } from '@/hooks/useSmartNotifications';
// Legacy pages (accessible via direct routes, not in main nav)
import YourGrowth from '@/pages/YourGrowth';
import IntegrationReport from '@/pages/IntegrationReport';
import SomaticMap from '@/pages/SomaticMap';
import IntegrationCircles from '@/pages/IntegrationCircles';
import MicroDosingCalendar from '@/pages/MicroDosingCalendar';
import HealingJourneys from '@/pages/HealingJourneys';
import VoiceIntegrity from '@/pages/VoiceIntegrity';
import ScientificFoundation from '@/pages/ScientificFoundation';
import ClinicalCompliance from '@/pages/ClinicalCompliance';
import ERIMethodology from '@/pages/ERIMethodology';
import Philosophy from '@/pages/Philosophy';
import Waitlist from '@/pages/Waitlist';
import AdminUsers from '@/pages/AdminUsers';
import AdminAnalytics from '@/pages/AdminAnalytics';
import TherapistReport from '@/pages/TherapistReport';
import FacilitatorOnboarding from '@/pages/FacilitatorOnboarding';
import RelationalPage from '@/pages/RelationalPage';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import { initTheme, watchSystemTheme } from '@/utils/theme';
import { Capacitor } from '@capacitor/core';
import ErrorBoundary from '@/components/ErrorBoundary';
import FeedbackButton from '@/components/FeedbackButton';
import MatrescenceOnboarding from '@/pages/MatrescenceOnboarding';
import Matrescence from '@/pages/Matrescence';
import EPDSAssessment from '@/pages/EPDSAssessment';
import EPDSHistory from '@/pages/EPDSHistory';

// Global scroll-to-top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navigation() {
  const location = useLocation();
  const { logout, user } = useAuth0();
  const [showMicroDosingNav, setShowMicroDosingNav] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  useEffect(() => {
    checkMicroDosingAccess();
  }, []);
  
  const checkMicroDosingAccess = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) return;
    
    try {
      const response = await axios.get(`${API}/users/${userEmail}`);
      const userData = response.data;
      // Show microdosing calendar only if user practices microdosing
      if (userData.expanded_states === 'yes' || userData.microdosing_practice === 'psilocybin' || userData.microdosing_practice === 'nootropics' || userData.microdosing_practice === 'expanded') {
        setShowMicroDosingNav(true);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin 
      } 
    });
  };
  
  // Hide navigation on onboarding pages
  if (['/onboarding', '/create-profile', '/assessment', '/proof', '/matrescence-onboarding'].includes(location.pathname)) {
    return null;
  }

  const userRole = localStorage.getItem('hypseya_user_role') || 'user';
  const userMode = localStorage.getItem('hypseya_user_mode') || 'standard';
  const isMatrescenceMode = userMode === 'matrescence';
  const isTherapistRole  = userRole === 'therapist';
  const isDoctorRole     = userRole === 'doctor';
  const isInstitutionRole = userRole === 'institution';
  const isProfessional   = isTherapistRole || isDoctorRole || isInstitutionRole;

  const professionalBase = isDoctorRole
    ? '/doctor-dashboard'
    : isInstitutionRole
    ? '/practitioner'
    : '/therapist-dashboard';

  const navItems = isProfessional ? [
    { path: professionalBase, label: 'Dashboard', iconKey: 'home'   },
    { path: '/studio',        label: 'Studio',    iconKey: 'studio' },
  ] : isMatrescenceMode ? [
    { path: '/matrescence',    label: 'Home',         iconKey: 'home'     },
    { path: '/journal',        label: 'Journal',      iconKey: 'journal'  },
    { path: '/insights',       label: 'Insights',     iconKey: 'insights' },
    { path: '/studio',         label: 'Studio',       iconKey: 'studio'   },
    { path: '/epds',           label: 'EPDS',         iconKey: 'circle'   },
  ] : [
    { path: '/',               label: 'Home',         iconKey: 'home'     },
    { path: '/journal',        label: 'Journal',      iconKey: 'journal'  },
    { path: '/insights',       label: 'Insights',     iconKey: 'insights' },
    { path: '/studio',         label: 'Studio',       iconKey: 'studio'   },
    { path: '/circles',        label: 'Circles',      iconKey: 'circle'   },
  ];

  return (
    <>
      {/* User Menu - Top Right */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <button
          onClick={() => setShowUserMenu(!showUserMenu)}
          aria-label="Account menu"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(30, 107, 104, 0.2)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            padding: 0
          }}
        >
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt="Profile" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover' 
              }} 
            />
          ) : (
            <User size={20} color="#1E6B68" />
          )}
        </button>
        
        {showUserMenu && (
          <div style={{
            position: 'absolute',
            top: '50px',
            right: '0',
            background: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(30, 107, 104, 0.2)',
            borderRadius: '12px',
            padding: '12px',
            minWidth: '180px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              padding: '8px',
              borderBottom: '1px solid rgba(30, 107, 104, 0.1)',
              marginBottom: '8px',
              fontSize: '0.9rem',
              color: '#1E6B68'
            }}>
              {user?.email}
            </div>
            <Link
              to="/settings"
              onClick={() => setShowUserMenu(false)}
              style={{
                display: 'block', width: '100%', padding: '10px',
                color: '#1E6B68', textDecoration: 'none',
                fontSize: '0.85rem', borderRadius: '8px',
                marginBottom: '6px', textAlign: 'center',
                background: 'rgba(30,107,104,0.06)'
              }}
            >
              Account Settings
            </Link>
            <Link
              to="/guide"
              onClick={() => setShowUserMenu(false)}
              style={{
                display: 'block', width: '100%', padding: '10px',
                color: '#1E6B68', textDecoration: 'none',
                fontSize: '0.85rem', borderRadius: '8px',
                marginBottom: '6px', textAlign: 'center',
                background: 'rgba(30,107,104,0.06)'
              }}
            >
              How it works
            </Link>
            <Link
              to="/privacy"
              onClick={() => setShowUserMenu(false)}
              style={{
                display: 'block', width: '100%', padding: '10px',
                color: '#1E6B68', textDecoration: 'none',
                fontSize: '0.85rem', borderRadius: '8px',
                marginBottom: '6px', textAlign: 'center',
                background: 'rgba(30,107,104,0.06)'
              }}
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              onClick={() => setShowUserMenu(false)}
              style={{
                display: 'block', width: '100%', padding: '10px',
                color: '#1E6B68', textDecoration: 'none',
                fontSize: '0.85rem', borderRadius: '8px',
                marginBottom: '6px', textAlign: 'center',
                background: 'rgba(30,107,104,0.06)'
              }}
            >
              Terms of Service
            </Link>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px',
                background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation — full-width chrome, items constrained so desktop
          doesn't splay five icons across the whole screen */}
      <nav data-testid="main-navigation" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'rgba(252, 249, 244, 0.96)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(184, 211, 207, 0.35)',
        display: 'flex', justifyContent: 'center',
        padding: '0 4px',
        paddingBottom: 'env(safe-area-inset-bottom)',
        zIndex: 900,
        boxShadow: '0 -4px 24px rgba(30,107,104,0.06)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'stretch',
          width: '100%', maxWidth: '560px',
        }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path === '/journal' && location.pathname === '/mind-mirror') ||
            (item.path === '/studio'  && ['/transformation-suite', '/programs'].includes(location.pathname));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.iconKey}`}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '10px 4px 12px',
                textDecoration: 'none', gap: '4px',
                position: 'relative'
              }}
            >
              {/* Active indicator dot */}
              {isActive && (
                <div style={{
                  position: 'absolute', top: 6, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 4, height: 4, borderRadius: '50%',
                  background: '#1E6B68', opacity: 0.7
                }} />
              )}
              {NavIcons[item.iconKey]?.(isActive)}
              <span style={{
                fontSize: '0.62rem',
                fontWeight: isActive ? '700' : '500',
                color: isActive ? '#1E6B68' : '#AAB8B6',
                letterSpacing: '0.02em',
                lineHeight: 1
              }}>
                {item.label}
              </span>
            </Link>
          );
        })}
        </div>
      </nav>
    </>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const isSyncingRef = useRef(false);
  // Intro: shown only once, before first registration
  const [introCompleted, setIntroCompleted] = useState(
    () => !!localStorage.getItem('hypseya_intro_seen')
  );

  useEffect(() => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    const checkAuth = async () => {
      if (isLoading) {
        isSyncingRef.current = false;
        return;
      }

      if (!isAuthenticated) {
        // Don't auto-redirect — show landing page instead
        setIsCheckingAuth(false);
        isSyncingRef.current = false;
        return;
      }

      // User is authenticated, sync with backend (once per session)
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.post(`${API}/auth/sync-user`, {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        setCurrentUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        localStorage.setItem('userEmail', response.data.email);

        // ── Role persistence ──────────────────────────────────────────────
        const clinicalRoles = ['therapist', 'doctor', 'institution', 'facilitator'];
        if (response.data.role && response.data.role !== 'user') {
          // Backend knows the clinical role — trust it
          localStorage.setItem('hypseya_user_role', response.data.role);
        } else {
          // Backend returned "user". Check if IntroExperience set a clinical role.
          const localRole = localStorage.getItem('hypseya_user_role');
          if (clinicalRoles.includes(localRole)) {
            try {
              await axios.patch(`${API}/users/me/role`, { role: localRole }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              response.data.role = localRole;
              localStorage.setItem('user', JSON.stringify(response.data));
              setCurrentUser({ ...response.data });
            } catch (_) {}
          } else {
            localStorage.removeItem('hypseya_user_role');
          }
        }

        // ── Mode persistence (matrescence) ────────────────────────────────
        if (response.data.mode && response.data.mode !== 'standard') {
          // Backend already knows the mode — sync to localStorage
          localStorage.setItem('hypseya_user_mode', response.data.mode);
        } else {
          // Backend says 'standard'. Check if IntroExperience set matrescence mode.
          const localMode = localStorage.getItem('hypseya_user_mode');
          if (localMode === 'matrescence') {
            try {
              await axios.patch(`${API}/users/me/mode`, { mode: 'matrescence' }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              response.data.mode = 'matrescence';
              localStorage.setItem('user', JSON.stringify(response.data));
              setCurrentUser({ ...response.data });
            } catch (_) {}
          } else {
            localStorage.removeItem('hypseya_user_mode');
          }
        }

        // Identify user in PostHog
        identifyUser(response.data.user_id || response.data.sub, response.data);
        Analytics.appOpened();

        // Register Web Push (browser notifications — no App Store needed)
        registerWebPush(response.data.user_id || response.data.sub);

        // ── Post-login routing ─────────────────────────────────────────────
        const isMatrescence = (response.data.mode || localStorage.getItem('hypseya_user_mode')) === 'matrescence';
        const matrescenceOnboarded = response.data.matrescence_profile?.intention;

        if (!response.data.has_completed_onboarding && location.pathname !== '/onboarding' && location.pathname !== '/create-profile') {
          if (isMatrescence) {
            navigate('/matrescence-onboarding');
          } else {
            navigate('/onboarding');
          }
        } else if (response.data.has_completed_onboarding && (location.pathname === '/' || location.pathname === '')) {
          const role = response.data.role || localStorage.getItem('hypseya_user_role') || 'user';
          if (role === 'therapist') {
            navigate('/therapist-dashboard');
          } else if (role === 'doctor') {
            navigate('/doctor-dashboard');
          } else if (role === 'institution') {
            navigate('/practitioner');
          } else if (isMatrescence && !matrescenceOnboarded) {
            navigate('/matrescence-onboarding');
          }
        }
      } catch (error) {
        console.error('Error syncing user:', error);
      } finally {
        setIsCheckingAuth(false);
        isSyncingRef.current = false;
      }
    };

    checkAuth();
  }, [isAuthenticated, isLoading]);

  if (isLoading || isCheckingAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #fafbfc 0%, #f0f7f6 40%, #e8f3f1 70%, #f5f8f7 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          width: 48, height: 48,
          border: '2px solid rgba(30,107,104,0.15)',
          borderTop: '2px solid #1E6B68',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Not authenticated — show intro portal (once) then landing page
  if (!isAuthenticated) {
    if (!introCompleted) {
      return (
        <IntroExperience
          onComplete={(action, role) => {
            setIntroCompleted(true);
            if (action === 'signup') {
              loginWithRedirect({ appState: { returnTo: '/', role }, screen_hint: 'signup' });
            } else if (action === 'login') {
              loginWithRedirect({ appState: { returnTo: location.pathname } });
            }
            // action === 'skip' → introCompleted is now true → LandingPage renders
          }}
        />
      );
    }
    return (
      <LandingPage
        onSignUp={() => loginWithRedirect({ appState: { returnTo: '/' }, screen_hint: 'signup' })}
        onLogin={() => loginWithRedirect({ appState: { returnTo: location.pathname } })}
      />
    );
  }

  return (
    <Routes>
      <Route path="/callback" element={<Callback />} />
      <Route path="/onboarding" element={<OnboardingRitual />} />
      <Route path="/create-profile" element={<CreateProfile />} />
      <Route path="/" element={<Home />} />
      <Route path="/alignment" element={<Alignment />} />
      {/* New nav routes */}
      <Route path="/journal" element={<MindMirror />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/studio" element={<TransformationSuite />} />
      {/* Legacy aliases — kept for deep links */}
      <Route path="/mind-mirror" element={<MindMirror />} />
      <Route path="/voice-lab" element={<Navigate to="/studio" replace />} />
      <Route path="/transformation-suite" element={<TransformationSuite />} />
      {/* Legacy routes - kept for backwards compatibility */}
      <Route path="/voice-integrity" element={<VoiceIntegrity />} />
      <Route path="/growth" element={<Navigate to="/insights" replace />} />
      <Route path="/microdosing" element={<MicroDosingCalendar />} />
      <Route path="/scientific-foundation" element={<ScientificFoundation />} />
      <Route path="/clinical-compliance" element={<ClinicalCompliance />} />
      <Route path="/eri-methodology" element={<ERIMethodology />} />
      <Route path="/healing-journeys" element={<HealingJourneys />} />
      <Route path="/philosophy" element={<Philosophy />} />
      <Route path="/living-room" element={<PsychedeliaLivingRoom />} />
      <Route path="/admin/psychedelia" element={<PsychedeliaAdmin />} />
      <Route path="/about" element={<About />} />
      <Route path="/window-of-tolerance" element={<WindowOfTolerance />} />
      <Route path="/pattern-intelligence" element={<PatternIntelligence />} />
      <Route path="/clinical-report" element={<ClinicalReport />} />
      <Route path="/practitioner" element={<PractitionerPortal />} />
      <Route path="/emotional-timeline" element={<EmotionalTimeline />} />
      <Route path="/therapist-dashboard" element={<TherapistDashboard />} />
      <Route path="/relational" element={<RelationalPage />} />
      <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
      <Route path="/my-meditations" element={<MyMeditations />} />
      <Route path="/assessment" element={<ClinicalAssessment />} />
      <Route path="/programs" element={<Programs />} />
      <Route path="/proof" element={<ProofOfChange />} />
      <Route path="/integration-report" element={<IntegrationReport />} />
      <Route path="/somatic-map" element={<SomaticMap />} />
      <Route path="/circles" element={<IntegrationCircles />} />
      <Route path="/guide" element={<Guide />} />
      {/* Matrescence mode */}
      <Route path="/matrescence" element={<Matrescence />} />
      <Route path="/matrescence-onboarding" element={<MatrescenceOnboarding />} />
      <Route path="/epds" element={<EPDSAssessment />} />
      <Route path="/epds-history" element={<EPDSHistory />} />
    </Routes>
  );
}

// ── Web Push registration (browser — works without App Store) ───────────────
async function registerWebPush(userId) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (!userId) return;

  try {
    // Register the service worker
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // Check existing subscription
    let sub = await reg.pushManager.getSubscription();

    if (!sub) {
      // Ask for permission + subscribe
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      // Fetch VAPID public key from backend
      const { data } = await axios.get(`${API}/web-push/vapid-public-key`);
      if (!data.public_key) return;

      // Convert base64url VAPID key to Uint8Array
      const vapidKey = data.public_key;
      const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawKey = Uint8Array.from(atob(base64), c => c.charCodeAt(0));

      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: rawKey,
      });
    }

    // Send subscription to backend
    await axios.post(`${API}/web-push/subscribe`, {
      user_id: userId,
      subscription: sub.toJSON(),
    });

  } catch (e) {
    console.warn('Web Push registration failed:', e);
  }
}

// ── Local notification daily reminder (Capacitor — no APNs/Firebase needed) ─
async function scheduleDailyLocalReminder() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') return;

    // Cancel existing so we don't pile up
    await LocalNotifications.cancel({ notifications: [{ id: 101 }] });

    // Schedule for 7:30pm today, or tomorrow if already past
    const now = new Date();
    const target = new Date();
    target.setHours(19, 30, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);

    const messages = [
      'Your practice is waiting. One session today.',
      'The nervous system responds to consistency, not intensity.',
      'Even 2 minutes keeps the thread alive.',
    ];
    const body = messages[new Date().getDay() % messages.length];

    await LocalNotifications.schedule({
      notifications: [{
        id: 101,
        title: 'HYPSEYA',
        body,
        schedule: { at: target, repeats: true, every: 'day' },
        sound: 'default',
        smallIcon: 'ic_stat_icon_config_sample',
      }],
    });
  } catch (e) {
    console.warn('Local notification scheduling failed:', e);
  }
}

// ── Push notification registration (Capacitor native only) ──────────────────
async function registerPushNotifications() {
  // Only runs on iOS / Android — silently skips on web
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return;

    await PushNotifications.register();

    PushNotifications.addListener('registration', async (token) => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user?.user_id) return;
        await axios.post(`${API}/device-token`, {
          user_id: user.user_id,
          token: token.value,
          platform: Capacitor.getPlatform(), // 'ios' | 'android'
        });
      } catch (e) {
        console.warn('Push token registration failed:', e);
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('Push registration error:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // Foreground notification — show as toast
      toast(notification.body || notification.title || 'HYPSEYA', {
        duration: 5000,
        style: { background: '#1E3A39', color: '#fff', borderRadius: '12px', fontSize: '0.88rem' },
      });
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      // User tapped notification — navigate if deep link present
      const data = action.notification.data;
      if (data?.route) {
        window.location.hash = data.route;
      }
    });
  } catch (e) {
    console.warn('Push notifications not available:', e);
  }
}

function App() {
  useEffect(() => {
    // Initialize analytics (PostHog)
    initAnalytics();

    // Initialize theme on app load
    initTheme();

    // Watch for system theme changes
    const cleanup = watchSystemTheme((theme) => {
      console.log('System theme changed to:', theme);
    });

    // Register push notifications (native only — no-op on web)
    registerPushNotifications();

    // Schedule daily local reminder (native only — no APNs/Firebase needed)
    scheduleDailyLocalReminder();

    // ERI-driven smart notifications
    initSmartNotifications();

    return cleanup;
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/eri-preview" element={<ERIPreview />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/settings" element={<AccountSettings />} />
            <Route path="/research" element={<Research />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/therapist-report/:token" element={<TherapistReport />} />
            <Route path="/facilitator" element={<FacilitatorOnboarding />} />
            <Route path="*" element={<><Navigation /><AppRoutes /></>} />
          </Routes>
          <MomentButton />
          <FeedbackButton />
          <Toaster position="top-center" />
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;